import React, { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiSearch,
  FiX,
  FiUsers,
  FiMessageSquare,
  FiLoader,
  FiArrowRight,
  FiGlobe,
  FiLock,
  FiTrendingUp,
  FiHash,
  FiAlertCircle,
  FiUserPlus,
} from "react-icons/fi";
import { useGlobalSearch } from "../../../services/communities/useGlobalSearch";
import { useThreadVote, useThreadDownvote, useSavedThreadIds, useUnsaveThread } from "../../../services/communities";
import ThreadCard from "../threads/ThreadCard";
import SaveThreadModal from "../feed/SaveThreadModal";
import "./SearchResultsSection.css";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.25,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

const tabVariants = {
  hidden: { opacity: 0, scale: 0.98 },
  visible: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.98 },
};

// Tab configuration
const SEARCH_TABS = [
  { id: "all", label: "All", icon: FiSearch },
  { id: "threads", label: "Threads", icon: FiMessageSquare },
  { id: "communities", label: "Communities", icon: FiUsers },
];

/**
 * SearchResultsSection - Displays global search results
 * Shows both communities and threads with tabbed navigation
 */
const SearchResultsSection = ({ query, onClearSearch }) => {
  const navigate = useNavigate();

  // State
  const [activeTab, setActiveTab] = useState("all");
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [selectedThreadForSave, setSelectedThreadForSave] = useState(null);

  // Search hook - uses unified /api/search/all endpoint
  const {
    communities,
    threads,
    totalResults,
    hasResults,
    isLoading,
    isSearching,
    isError,
    error,
    isQueryTooShort,
  } = useGlobalSearch(query);

  // Voting mutations
  const voteThreadMutation = useThreadVote();
  const downvoteThreadMutation = useThreadDownvote();
  const unsaveThreadMutation = useUnsaveThread();
  
  // Saved thread IDs for checking saved status
  const { data: savedData } = useSavedThreadIds();

  // Format member count
  const formatMemberCount = useCallback((count) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count?.toString() || "0";
  }, []);

  // Format time ago
  const formatTimeAgo = useCallback((dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d`;

    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }, []);

  // Handle thread click
  const handleThreadClick = useCallback(
    (thread) => {
      navigate(`/communities/${thread.community._id}/threads/${thread._id}`);
    },
    [navigate]
  );

  // Handle community click
  const handleCommunityClick = useCallback(
    (community) => {
      const isMember = community.userMembership?.role;
      if (isMember) {
        navigate(`/communities/${community._id}`);
      } else {
        navigate(`/communities/${community._id}/preview`);
      }
    },
    [navigate]
  );

  // Handle voting
  const handleVote = useCallback(
    (threadId) => {
      const thread = threads.find((t) => t._id === threadId);
      if (!thread) return;
      voteThreadMutation.mutate({
        communityId: thread.community._id,
        threadId,
      });
    },
    [threads, voteThreadMutation]
  );

  const handleDownvote = useCallback(
    (threadId) => {
      const thread = threads.find((t) => t._id === threadId);
      if (!thread) return;
      downvoteThreadMutation.mutate({
        communityId: thread.community._id,
        threadId,
      });
    },
    [threads, downvoteThreadMutation]
  );

  // Handle save/unsave toggle
  const handleSave = useCallback(
    async (thread) => {
      if (thread.isSaved) {
        // Thread is already saved - unsave it directly
        try {
          await unsaveThreadMutation.mutateAsync({ threadId: thread._id });
        } catch (error) { /* ignored */ }
      } else {
        // Thread is not saved - open modal for collection selection
        setSelectedThreadForSave(thread);
        setShowSaveModal(true);
      }
    },
    [unsaveThreadMutation]
  );

  // Enhance threads with saved status
  const enhancedThreads = useMemo(() => {
    return threads.map((thread) => ({
      ...thread,
      isSaved: savedData?.savedSet?.has(thread._id) ?? thread.userHasSaved ?? false,
    }));
  }, [threads, savedData]);

  // Filter results based on active tab
  const filteredThreads = useMemo(
    () => (activeTab === "communities" ? [] : enhancedThreads),
    [activeTab, enhancedThreads]
  );

  const filteredCommunities = useMemo(
    () => (activeTab === "threads" ? [] : communities),
    [activeTab, communities]
  );

  // Tab counts
  const tabCounts = useMemo(
    () => ({
      all: totalResults,
      threads: threads.length,
      communities: communities.length,
    }),
    [totalResults, threads.length, communities.length]
  );

  // Community Card Component
  const CommunityCard = ({ community }) => {
    const isMember = community.userMembership?.role;

    return (
      <motion.div
        className="srs-community-card"
        variants={itemVariants}
        onClick={() => handleCommunityClick(community)}
        whileHover={{ y: -3, scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
      >
        <div className="srs-community-avatar">
          {community.signedPhotoURL || community.photoURL ? (
            <img
              src={community.signedPhotoURL || community.photoURL}
              alt={community.name}
            />
          ) : (
            <FiUsers size={20} />
          )}
        </div>

        <div className="srs-community-content">
          <div className="srs-community-header">
            <h4 className="srs-community-title">{community.name}</h4>
            <div className="srs-community-badges">
              <span className={`srs-type-badge ${community.type}`}>
                {community.type === "public" ? (
                  <FiGlobe size={10} />
                ) : (
                  <FiLock size={10} />
                )}
                {community.type}
              </span>
            </div>
          </div>

          <p className="srs-community-description">
            {community.description?.slice(0, 100)}
            {community.description?.length > 100 ? "..." : ""}
          </p>

          <div className="srs-community-footer">
            <div className="srs-community-stats">
              <span>
                <FiUsers size={12} />
                {formatMemberCount(community.memberCount)} members
              </span>
              {community.field && (
                <span className="srs-field-tag">
                  <FiHash size={10} />
                  {community.field}
                </span>
              )}
            </div>
            <button
              className={`srs-community-action ${isMember ? "member" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                handleCommunityClick(community);
              }}
            >
              {isMember ? (
                <>
                  <FiArrowRight size={14} />
                  <span>Open</span>
                </>
              ) : (
                <>
                  <FiUserPlus size={14} />
                  <span>View</span>
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="srs-container">
      {/* Search Header */}
      <motion.div
        className="srs-header"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="srs-header-left">
          <div className="srs-search-icon">
            <FiSearch size={20} />
          </div>
          <div className="srs-header-text">
            <h2>
              Search Results
              {query && <span className="srs-query">for "{query}"</span>}
            </h2>
            {hasResults && !isSearching && (
              <p className="srs-results-count">
                Found {totalResults} results
                {threads.length > 0 && ` • ${threads.length} threads`}
                {communities.length > 0 && ` • ${communities.length} communities`}
              </p>
            )}
          </div>
        </div>

        <motion.button
          className="srs-clear-btn"
          onClick={onClearSearch}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FiX size={16} />
          <span>Clear</span>
        </motion.button>
      </motion.div>

      {/* Tabs */}
      <div className="srs-tabs">
        {SEARCH_TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          const count = tabCounts[tab.id];

          return (
            <motion.button
              key={tab.id}
              className={`srs-tab ${isActive ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <Icon size={16} />
              <span>{tab.label}</span>
              {count > 0 && <span className="srs-tab-count">{count}</span>}
              {isActive && (
                <motion.div
                  className="srs-tab-indicator"
                  layoutId="searchTab"
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              )}
            </motion.button>
          );
        })}
      </div>

      {/* Results Content */}
      <div className="srs-content">
        {/* Loading State */}
        {isLoading || isSearching ? (
          <motion.div
            className="srs-state loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="srs-loader">
              <FiLoader className="srs-spinner" size={32} />
            </div>
            <h3>Searching...</h3>
            <p>Finding threads and communities matching "{query}"</p>
          </motion.div>
        ) : isQueryTooShort ? (
          /* Query Too Short */
          <motion.div
            className="srs-state hint"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <FiSearch size={48} />
            <h3>Keep typing...</h3>
            <p>Enter at least 2 characters to search</p>
          </motion.div>
        ) : isError ? (
          /* Error State */
          <motion.div
            className="srs-state error"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <FiAlertCircle size={48} />
            <h3>Search failed</h3>
            <p>{error?.message || "Something went wrong"}</p>
            <button className="srs-retry-btn" onClick={onClearSearch}>
              Try again
            </button>
          </motion.div>
        ) : !hasResults && query.length >= 2 ? (
          /* Empty State */
          <motion.div
            className="srs-state empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <FiSearch size={48} />
            <h3>No results found</h3>
            <p>
              We couldn't find anything matching "{query}".
              <br />
              Try different keywords or check your spelling.
            </p>
            <button className="srs-clear-search-btn" onClick={onClearSearch}>
              Clear search
            </button>
          </motion.div>
        ) : (
          /* Results */
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              variants={tabVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="srs-results"
            >
              {/* Communities Section */}
              {filteredCommunities.length > 0 && (
                <div className="srs-section">
                  <div className="srs-section-header">
                    <FiUsers size={16} />
                    <h3>Communities</h3>
                    <span className="srs-section-count">
                      {filteredCommunities.length}
                    </span>
                  </div>
                  <motion.div
                    className="srs-communities-grid"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    {filteredCommunities.map((community) => (
                      <CommunityCard key={community._id} community={community} />
                    ))}
                  </motion.div>
                </div>
              )}

              {/* Threads Section */}
              {filteredThreads.length > 0 && (
                <div className="srs-section">
                  <div className="srs-section-header">
                    <FiMessageSquare size={16} />
                    <h3>Threads</h3>
                    <span className="srs-section-count">
                      {filteredThreads.length}
                    </span>

                  </div>
                  <motion.div
                    className="srs-threads-list"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    {filteredThreads.map((thread) => (
                      <motion.div
                        key={thread._id}
                        variants={itemVariants}
                        className="srs-thread-wrapper"
                      >
                        <ThreadCard
                          thread={thread}
                          communityId={thread.community?._id}
                          onThreadClick={handleThreadClick}
                          onCommunityClick={handleCommunityClick}
                          onVote={handleVote}
                          onDownvote={handleDownvote}
                          onSave={handleSave}
                          showCommunityInfo={true}
                          formatTimeAgo={formatTimeAgo}
                        />
                      </motion.div>
                    ))}
                  </motion.div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      {/* Save Thread Modal */}
      <AnimatePresence>
        {showSaveModal && selectedThreadForSave && (
          <SaveThreadModal
            thread={selectedThreadForSave}
            onClose={() => {
              setShowSaveModal(false);
              setSelectedThreadForSave(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchResultsSection;
